import { Injectable } from '@angular/core'
import {
  AnnotationsInternalPositionsClient,
  CreatePositionRequest,
  OrderPositionsRequest,
  Position,
  PositionDetail,
} from '@ftr/annotations-contracts'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { ApiResult, RemoteData } from '@ftr/foundation'
import { filter } from 'rxjs'
import { AnnotationsApiClientFactory } from '../../api-client'
import { InternalPositionInUseError } from '../../api-client/errors'
import { AnnotationsService } from '../base'

@Injectable({
  providedIn: 'root',
})
export class InternalPositionsService extends AnnotationsService {
  constructor(apiClientFactory: AnnotationsApiClientFactory, regionCache: CourtSystemRegionCache) {
    super(apiClientFactory, regionCache)
  }

  /**
   * @param courtSystemId
   * @param involvement
   * @returns Court staff internal positions within a court system.
   */
  getInternalPositions(courtSystemId: Uuid, involvement: PositionDetail['involvement']): ApiResult<PositionDetail[]> {
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsInternalPositionsClient['listDetailedPositions']>>,
      PositionDetail[]
    >({
      courtSystemId,
      requestFn: client =>
        client.internalPositions.listDetailedPositions({
          query: {
            courtSystemId,
            involvement,
          },
        }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to fetch positions'))
        }

        return RemoteData.success<PositionDetail[]>(result.body)
      },
    }).pipe(filter(x => !x.isLoading()))
  }

  createInternalPosition(body: CreatePositionRequest): ApiResult<Position> {
    return this.performApiRequest({
      courtSystemId: body.courtSystemId,
      requestFn: client => client.internalPositions.createPosition({ body }),
      responseMapFn: result => {
        if (result.status === 409) {
          return RemoteData.failure(new Error('A position with this name already exists'))
        }
        if (result.status !== 201) {
          return RemoteData.failure(new Error('Failed to create position'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  updateInternalPosition(positionId: string, name: string, courtSystemId: string): ApiResult<Position> {
    return this.performApiRequest({
      courtSystemId,
      requestFn: client =>
        client.internalPositions.updatePosition({
          params: { positionId },
          body: { name },
        }),
      responseMapFn: result => {
        if (result.status === 409) {
          return RemoteData.failure(new Error('A position with this name already exists'))
        }
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to update position'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  deleteInternalPosition(positionId: Uuid, courtSystemId: Uuid): ApiResult<void> {
    return this.performApiRequest({
      courtSystemId,
      requestFn: client => client.internalPositions.deletePosition({ params: { positionId } }),
      responseMapFn: result => {
        if (result.status === 409) {
          return RemoteData.failure(new InternalPositionInUseError())
        }
        if (result.status !== 204) {
          return RemoteData.failure(new Error('Failed to delete position'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  orderInternalPositions(body: OrderPositionsRequest, courtSystemId: Uuid): ApiResult<PositionDetail[]> {
    return this.performApiRequest({
      courtSystemId,
      requestFn: client => client.internalPositions.orderPositions({ body, query: { courtSystemId } }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to order positions'))
        }

        return RemoteData.success(result.body)
      },
    })
  }
}
